
import { getUserTeam } from "~/fc/Dal/TeamRepo";
import { getUserSubscription } from "~/fc/Dal/PaymentRepo";
import UrlParamHandler from "~/fc/UrlParamHandler";
import ToastManager from '~/fc/ToastManager';
import MetaHead from "../mixins/MetaHead";
import { initiatePricingPageLocale } from "~/fc/LocaleHelper";

import { formatPlansForPricingPageDisplay, getPricingPageData, adjustPricingPlansForLocale } from "~/fc/PaymentHelper";
import { LOCALIZED_PRICING_ROUTES_FOR_COUNTRIES } from "~/config/subscription.config";

export default {
    name: "PricingPage",
    components: {
        Plans: () => import(/* webpackChunkName: "Plans" */ "../layouts/partials/pricing/Plan"),
        PlanSwitcher: () => import(/* webpackChunkName: "PlanSwitcher" */ "../layouts/partials/pricing/Plan/PlanSwitcher"),
        Partners: () => import(/* webpackChunkName: "Partners" */ "../layouts/partials/pricing/Plan/Partners.vue"),
        BannerContainer: () => import(/* webpackChunkName: "BannerContainer" */ "../components/ui/BannerContainer"),
        Accordion: () => import(/* webpackChunkName: "Accordion" */ "../components/ui/Accordion"),
        // TrustPilotReviews: () => import(/* webpackChunkName: "Accordion" */ "~/components/ui/TrustPilotReviews"),
        SecurityAndCompliance: () => import(/* webpackChunkName: "SecurityAndCompliance" */ "~/components/ui/SecurityAndCompliance"),
    },
    head() {
        const meta = []
        const restrictedLocales = ["en-in", "en-id", "en-ph", "en-br", "en-my"];
        const { locale } = this.$i18n; // Get current locale
        if (restrictedLocales.includes(locale) && this.$route.path.startsWith(`/${locale}`)) {
            meta.push({ hid: 'robots', name: 'robots', content: 'noindex, nofollow' })
        }
        return { meta: meta }
    },
    mixins: [MetaHead],
    beforeRouteEnter(to, from, next) {
        if (from.name &&
            !from.name.includes("auth") &&
            from.matched &&
            from.matched[0] &&
            from.matched[0].instances &&
            from.matched[0].instances.default
        ) {
            const store = from.matched[0].instances.default.$store;
            store.commit("setRedirect", from.fullPath);
        }
        next();
    },
    async asyncData({ app, store }) {
        const [faqs, plans, reviews] = await getPricingPageData(app, store)
        const [unifiedAnnualPlans, unifiedPlans] = formatPlansForPricingPageDisplay(plans)
        return { unifiedAnnualPlans, unifiedPlans, faqs, reviews };
    },
    data() {
        return {
            title: "Pricing and Plans",
            desc: "Pricing information for FreeConvert.com pro packages.",
            checkoutUrl: "/checkout",
            unifiedPlans: [],
            faqs: null,
            thumbSize: 0,
            tools: [],
            isMember: false,
            teamInfo: {},
            showLegacyAlert: false,
            planType: 'monthly',
            subscriptions: [],
            currentSubscription: {},
            userSubscriptionType: 'free',
            reviews: []
        }
    },
    computed: {
        banner() { return this.$store.state.banner },
        user() { return this.$auth ? this.$auth.User : {} },
        toastManager() { return new ToastManager(this) },
        country() { return this.$store.state.store.country },
        canChange() {
            if (!this.teamInfo || !this.isMember) return true;
            return this.isMember && this.teamInfo.owner === this.user.id;
        },
        userSubscription() { return this.$store.state.subscription },
        userSubscriptionCurrency() { return this.$store.state.subscription?.plan?.currency?.currencyCode || 'USD' },
        /**
        * Determines if a user is an USD package is on a localized pricing page.
        *
        * @returns {boolean} True if the user has a USD package and is not on a localized pricing page; otherwise, false.
        */
        isNoneLocaleUserOnLocalePage() {
            if (!this.userSubscription.subscription || !this.userSubscription.subscription.hasOwnProperty('isSelectedCurrencyAllowed')) return false
            return !this.userSubscription.subscription?.isSelectedCurrencyAllowed;
        },
        isMobile () { return this.$store.state.store.isMobile }
    },
    watch: {
        canChange(val) {
            this.$store.commit("updateCanChangeSubscription", val);
        },
        userSubscriptionType(val) {
            this.planType = val === 'annual' ? val : 'monthly'
        }
    },
    async mounted() {
        await initiatePricingPageLocale(this)
        await adjustPricingPlansForLocale(this)

        this.$nextTick(() => { this.handleContactForm() })
        const { code: coupon, value } = this.$route.query;
        if (coupon && value) {
            const validity = Number(value.split("-")[0]);
            const now = Date.now();
            if (now <= validity) {
                const cookieOptions = { expires: new Date(validity), path: "/" };
                this.$cookies.set("applied_coupon_from_email", coupon, cookieOptions);
                this.$cookies.set("coupon_discount_percentage", 30, cookieOptions);
            } else {
                this.toastManager.add("danger", "Your coupon has expired", this.$t("coupon_has_expired"));
                $nuxt.$cookies.remove("applied_coupon_from_email");
            }
        }
        this.$store.commit('banner/removeFreeLimitPerFileWarn');
        new UrlParamHandler(this.$route).handle()

        if (this.$auth.Token) this.loadTeamInfo();
        else localStorage.removeItem("currentSubscriptions");

        if (process.client) {
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) this.initiatePricingPage()
            });
            this.initiatePricingPage()
        }
    },
    beforeDestroy() {
        if (window) window.removeEventListener('pageshow', () => {})
        if (window && window.wisepops) {
            const wisepops_props = this.$cookies.get("wisepops_props") || {};
            this.$cookies.remove("wisepops_props");
            wisepops("properties", {
                ...wisepops_props,
                isPricingPage: false
            });
            wisepops("pageview");
        }
        this.$store.commit("banner/clearAllBanners");
    },
    methods: {
        determineUserSubscriptionType() {
            if (this.currentSubscription["unified-subscription"]) {
                if (this.currentSubscription["unified-subscription"]?.plan?.group?.includes('annual')) {
                    this.userSubscriptionType = 'annual'
                    return
                }
                this.userSubscriptionType = 'monthly'
            } else if (this.currentSubscription["unified-credit"]) {
                this.userSubscriptionType = 'credit'
            } else {
                this.userSubscriptionType = 'free'
            }
        },
        async getUserAllActiveSubscriptions() {
            try {
                const { data: subscriptions } = await getUserSubscription(this.$auth.Token);

                if (!subscriptions || !subscriptions.length) {
                    localStorage.removeItem("currentSubscriptions");
                    this.handleShowLegacyAlertUpdate(false);
                    return;
                }

                let hasApiCredit = false
                let legacySubCount = 0

                subscriptions.forEach((sub) => {
                    if (sub.status === "active") {
                        this.currentSubscription[sub.plan.type] = {
                            currentPeriodEnd: sub.currentPeriodEnd,
                            plan: {
                                group: sub.plan.group,
                            },
                            remoteId: sub.remoteId,
                            paymentMethod: sub.paymentMethod
                        };

                        switch (sub.plan.type) {
                            case "api-credit":
                                hasApiCredit = true;
                                break;
                            case "web":
                            case "api":
                                legacySubCount++;
                                break;
                        }
                    }
                });

                // we will display manual proration warning banner if user has multiple legacy subscriptions
                // or has api-credit
                this.handleShowLegacyAlertUpdate(legacySubCount > 1 || hasApiCredit);

                localStorage.setItem(
                    "currentSubscriptions",
                    JSON.stringify(this.currentSubscription)
                );

                this.determineUserSubscriptionType();
                this.subscriptions = subscriptions;
            } catch (error) {
                console.log('error in loading subscription info', error);
            }
        },
        async initiatePricingPage() {
            try {
                if (this.$auth.Token) {
                    await this.getUserAllActiveSubscriptions();
                }
                // don't load the script on mobile devices or if the user is from Localized Country
                const country = this.$store.state.store.country
                if (this.isMobile || (country && LOCALIZED_PRICING_ROUTES_FOR_COUNTRIES[country])) {
                    this.$cookies.remove("wisepops_props");
                    return
                }
                // it's important to call this function after we fetch the user's subscription info
                this.setupPricingPageVisit();
            } catch (e) {
            }
        },
        handleShowLegacyAlertUpdate(value) {
            this.showLegacyAlert = value;
            setTimeout(() => { this.handleContactForm() }, 500)
        },
        loadWisePopsScript() {
            const src = process.env.WISEPOPS_URL;
            if (!src) return;

            const script = document.createElement('script');
            script.defer = true;
            script.src = src;
            document.head.appendChild(script);
            script.onload = () => {
                this.$bus.$emit('WISEPOPS_LOADED');
                this.setupWisePopsParams();
            };
        },
        async setupPricingPageVisit() {
            try {
                let isPro = Boolean(this.subscriptions.length);
                const expires = new Date(Date.now() + 86400 * 1000);
                const visitCount = parseInt(this.$cookies.get("pricing_page_visit_count") || 0) + 1;
                const couponApplied = !!(this.$cookies.get("applied_coupon"))
                const cookieOptions = { secure: false, expires, path: "/" };

                // set necessary cookies
                this.$cookies.set("pricing_page_visit_count", visitCount, cookieOptions);
                this.$cookies.set("pro_user", isPro, cookieOptions);

                if (!window.wisepops && visitCount > 2 && !isPro && !couponApplied) {
                    this.loadWisePopsScript();
                }
            } catch (error) {
                console.log('wisepops_error', error);
            }
        },
        setupWisePopsParams() {
            const isPro = this.$cookies.get("pro_user") || false;
            const wisepops_props = this.$cookies.get("wisepops_props") || {};
            const visitCount = parseInt(this.$cookies.get("pricing_page_visit_count") || 0)
            const couponApplied = !!(this.$cookies.get("applied_coupon"))

            this.$cookies.remove("wisepops_props");
            wisepops("properties", {
                ...wisepops_props,
                alreadySubscribed: isPro,
                pricingPageVisit: visitCount,
                couponApplied,
                isPricingPage: true,
            });
            wisepops("pageview");
        },
        onPlanSelect(item) {
            this.$store.commit("setTmpPlan", item);
            localStorage.setItem("selectedPlan", JSON.stringify(item));
            if (this.$auth.Token) {
                this.$router.push(this.localePath(this.checkoutUrl));
            } else {
                localStorage.setItem('redirect', this.localePath(this.checkoutUrl));
                this.$router.push(this.localePath("/auth/signup"));
            }
        },
        async loadTeamInfo() {
            try {
                const { data } = await getUserTeam(this.$auth.Token);
                if (data && data[0] && data[0].currentUserRole !== "OWNER") {
                    this.isMember = true;
                    this.teamInfo = data[0].team;
                }
            } catch (e) { }

        },
        closeAlert(id) {
            document.getElementById(id).style.display = "none";
        },
        handleContactForm() {
            const self = this
            const contactLink = document.querySelectorAll('.FWTrigger')
            for (let i = 0; i < contactLink.length; i++) {
                contactLink[i].addEventListener('click', function (event) {
                    event.preventDefault()
                    self.$bus.$emit('OPEN_FW')
                })
            }
        }
    }
};
